import { useState, useRef, useEffect, useMemo } from "react";
import { SlidersHorizontal, X, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SCORE_PRESETS = [
  { key: 'bearish', label: 'BEARISH' },
  { key: 'bullish', label: 'BULLISH' },
  { key: 'strong', label: 'STRONG' },
  { key: 'extreme', label: 'EXTREME' },
];

function FilterChip({ label, onRemove, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[11px] tracking-[0.06em] uppercase"
      style={{
        backgroundColor: 'rgba(74, 222, 128, 0.08)',
        color: '#4ade80',
        border: '1px solid rgba(74, 222, 128, 0.3)',
      }}
    >
      {label}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="hover:opacity-100 opacity-60 transition-opacity"
        style={{ color: '#4ade80' }}
      >
        <X size={10} />
      </button>
    </span>
  );
}

function AddDropdown({ options, onSelect, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        data-testid={testId}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[11px] transition-colors"
        style={{
          color: '#484f58',
          border: '1px dashed #30363d',
          backgroundColor: 'transparent',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#8b949e'; e.currentTarget.style.borderColor = '#484f58'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; e.currentTarget.style.borderColor = '#30363d'; }}
      >
        <Plus size={10} /> Add
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-50 rounded-md border overflow-hidden min-w-[160px] max-h-[200px] overflow-y-auto"
          style={{ backgroundColor: '#1c2333', borderColor: '#30363d' }}
        >
          {options.map(opt => (
            <button
              key={opt}
              data-testid={`filter-dropdown-option-${opt}`}
              onClick={(e) => { e.stopPropagation(); onSelect(opt); setOpen(false); }}
              className="block w-full text-left px-3 py-1.5 font-mono text-xs transition-colors"
              style={{ color: '#c9d1d9' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#21262d'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span
        className="font-mono text-[10px] tracking-[0.12em] uppercase shrink-0 pt-0.5"
        style={{ color: '#484f58', minWidth: '76px' }}
      >
        {label}
      </span>
      <div className="flex items-center flex-wrap gap-1.5">
        {children}
      </div>
    </div>
  );
}

export default function FilterBar({
  filters,
  onToggleFilter,
  onSetScorePreset,
  onClearAll,
  availableInstruments,
  availableSources,
  availableTags,
  activeFilterCount,
}) {
  const [expanded, setExpanded] = useState(false);
  const prevCountRef = useRef(0);
  const [overflowVisible, setOverflowVisible] = useState(false);

  useEffect(() => {
    if (!expanded) setOverflowVisible(false);
  }, [expanded]);

  useEffect(() => {
    if (prevCountRef.current === 0 && activeFilterCount > 0) {
      setExpanded(true);
    }
    prevCountRef.current = activeFilterCount;
  }, [activeFilterCount]);

  const remainingInstruments = useMemo(
    () => availableInstruments.filter(i => !filters.instruments.includes(i)),
    [availableInstruments, filters.instruments]
  );
  const remainingSources = useMemo(
    () => availableSources.filter(s => !filters.sources.includes(s)),
    [availableSources, filters.sources]
  );
  const remainingTags = useMemo(
    () => availableTags.filter(t => !filters.tags.includes(t)),
    [availableTags, filters.tags]
  );

  const scoreButtonStyle = (isActive) => isActive
    ? {
        backgroundColor: 'rgba(74, 222, 128, 0.12)',
        color: '#4ade80',
        border: '1px solid rgba(74, 222, 128, 0.4)',
      }
    : {
        backgroundColor: 'transparent',
        color: '#8b949e',
        border: '1px solid #30363d',
      };

  return (
    <div
      data-testid="filter-bar"
      className="mb-3 sm:mb-4 rounded-[10px] border"
      style={{ backgroundColor: '#111827', borderColor: '#30363d' }}
    >
      <div
        className="flex items-center justify-between px-3 sm:px-4 py-2.5 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
        data-testid="filter-bar-toggle"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={13} style={{ color: '#8b949e' }} />
          <span
            className="font-mono text-[11px] tracking-[0.12em] uppercase font-medium"
            style={{ color: '#8b949e' }}
          >
            Filters
          </span>
          {activeFilterCount > 0 && (
            <span
              data-testid="filter-active-count"
              className="font-mono text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: 'rgba(74, 222, 128, 0.12)',
                color: '#4ade80',
              }}
            >
              {activeFilterCount} active
            </span>
          )}
          {expanded ? (
            <ChevronUp size={12} style={{ color: '#484f58' }} />
          ) : (
            <ChevronDown size={12} style={{ color: '#484f58' }} />
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            data-testid="filter-clear-all"
            onClick={(e) => { e.stopPropagation(); onClearAll(); }}
            className="font-mono text-[11px] tracking-[0.08em] uppercase transition-opacity hover:opacity-100 opacity-70"
            style={{ color: '#ef4444' }}
          >
            Clear All
          </button>
        )}
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: overflowVisible ? 'visible' : 'hidden' }}
            onAnimationComplete={() => { if (expanded) setOverflowVisible(true); }}
          >
            <div
              className="px-3 sm:px-4 pb-3 pt-1 space-y-0.5"
              style={{ borderTop: '1px solid #1c2333' }}
            >
              <FilterRow label="Instrument">
                {filters.instruments.map(inst => (
                  <FilterChip
                    key={inst}
                    label={inst}
                    testId={`filter-chip-instrument-${inst}`}
                    onRemove={() => onToggleFilter('instruments', inst)}
                  />
                ))}
                <AddDropdown
                  options={remainingInstruments}
                  onSelect={(val) => onToggleFilter('instruments', val)}
                  testId="filter-add-instrument"
                />
              </FilterRow>

              <FilterRow label="Score">
                {SCORE_PRESETS.map(preset => (
                  <button
                    key={preset.key}
                    data-testid={`filter-score-${preset.key}`}
                    onClick={(e) => { e.stopPropagation(); onSetScorePreset(filters.scorePreset === preset.key ? null : preset.key); }}
                    className="px-2.5 py-0.5 rounded-full font-mono text-[11px] tracking-[0.06em] uppercase transition-all duration-150"
                    style={scoreButtonStyle(filters.scorePreset === preset.key)}
                  >
                    {preset.label}
                  </button>
                ))}
                {filters.scorePreset && (
                  <button
                    data-testid="filter-score-all"
                    onClick={(e) => { e.stopPropagation(); onSetScorePreset(null); }}
                    className="px-2 py-0.5 rounded-full font-mono text-[10px] tracking-[0.06em] uppercase transition-opacity hover:opacity-100 opacity-60"
                    style={{ color: '#8b949e', border: '1px solid #30363d' }}
                  >
                    ALL
                  </button>
                )}
              </FilterRow>

              <FilterRow label="Source">
                {filters.sources.map(src => (
                  <FilterChip
                    key={src}
                    label={src}
                    testId={`filter-chip-source-${src.replace('@', '')}`}
                    onRemove={() => onToggleFilter('sources', src)}
                  />
                ))}
                <AddDropdown
                  options={remainingSources}
                  onSelect={(val) => onToggleFilter('sources', val)}
                  testId="filter-add-source"
                />
              </FilterRow>

              <FilterRow label="Tags">
                {filters.tags.map(tag => (
                  <FilterChip
                    key={tag}
                    label={tag}
                    testId={`filter-chip-tag-${tag}`}
                    onRemove={() => onToggleFilter('tags', tag)}
                  />
                ))}
                <AddDropdown
                  options={remainingTags}
                  onSelect={(val) => onToggleFilter('tags', val)}
                  testId="filter-add-tag"
                />
              </FilterRow>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
